import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '~/lib/api';

interface Memoria {
  _id: string;
  scope: 'global' | 'vendor' | 'invoice_template' | 'category';
  condition: string;
  action: string;
  vendor_id?: string;
  vendor_name?: string;
  account_id?: string;
  evidence_count?: number;
  confidence: 'high' | 'medium' | 'low';
  source: 'historical_seed' | 'operator_manual' | 'operator_correction';
  is_active: boolean;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

type ScopeFilter = 'all' | Memoria['scope'];

const SCOPE_LABEL: Record<Memoria['scope'], string> = {
  global: 'Globales',
  vendor: 'Por proveedor',
  invoice_template: 'Proveedores recurrentes',
  category: 'Por categoría',
};

const SCOPE_DESCRIPTION: Record<Memoria['scope'], string> = {
  global: 'Reglas que aplican a cualquier factura, sin importar proveedor.',
  vendor: 'Mapeo proveedor → cuenta contable. La IA la aplica cuando la factura coincide con ese proveedor.',
  invoice_template:
    'Proveedores que facturan habitualmente cada mes. La IA las reconoce como facturas esperables y puede sugerir pre-aprobación si el monto se parece al histórico.',
  category: 'Reglas que actúan sobre categorías de cuenta contable.',
};

const CONFIDENCE_BADGE: Record<Memoria['confidence'], string> = {
  high: 'badge-success',
  medium: 'badge-warning',
  low: 'badge-ghost',
};

const CONFIDENCE_LABEL: Record<Memoria['confidence'], string> = {
  high: 'Alta',
  medium: 'Media',
  low: 'Baja',
};

const SOURCE_LABEL: Record<Memoria['source'], string> = {
  historical_seed: 'Histórico NS',
  operator_manual: 'Manual',
  operator_correction: 'Corrección',
};

type Draft = {
  scope: Memoria['scope'];
  condition: string;
  action: string;
  vendor_name: string;
  notes: string;
  confidence: Memoria['confidence'];
};

function emptyDraft(): Draft {
  return {
    scope: 'global',
    condition: '',
    action: '',
    vendor_name: '',
    notes: '',
    confidence: 'medium',
  };
}

function draftFrom(m: Memoria): Draft {
  return {
    scope: m.scope,
    condition: m.condition,
    action: m.action,
    vendor_name: m.vendor_name ?? '',
    notes: m.notes ?? '',
    confidence: m.confidence,
  };
}

export default function MemoriasPage() {
  const [memorias, setMemorias] = useState<Memoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [activeOnly, setActiveOnly] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);

  // Edit / delete state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(emptyDraft());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Create state
  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<Draft>(emptyDraft());
  const [createError, setCreateError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/tbit-invoices/api/memorias');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `Error ${res.status}`);
      }
      const data = await res.json();
      setMemorias(data.memorias ?? []);
    } catch (err: any) {
      setError(err.message || 'No se pudieron cargar las memorias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (m: Memoria) => {
    setSavingId(m._id);
    const previousValue = m.is_active;
    setMemorias((curr) => curr.map((x) => (x._id === m._id ? { ...x, is_active: !previousValue } : x)));
    try {
      const res = await apiFetch(`/tbit-invoices/api/memorias/${m._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !previousValue }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
    } catch (err) {
      setMemorias((curr) => curr.map((x) => (x._id === m._id ? { ...x, is_active: previousValue } : x)));
      console.error('Toggle failed:', err);
    } finally {
      setSavingId(null);
    }
  };

  const startEdit = (m: Memoria) => {
    setEditingId(m._id);
    setEditDraft(draftFrom(m));
    setConfirmDeleteId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setConfirmDeleteId(null);
  };

  const saveEdit = async (id: string) => {
    setSavingId(id);
    try {
      const body: Record<string, unknown> = {
        condition: editDraft.condition.trim(),
        action: editDraft.action.trim(),
        notes: editDraft.notes,
        confidence: editDraft.confidence,
      };
      const res = await apiFetch(`/tbit-invoices/api/memorias/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `Error ${res.status}`);
      }
      setMemorias((curr) =>
        curr.map((x) =>
          x._id === id
            ? {
                ...x,
                condition: editDraft.condition.trim(),
                action: editDraft.action.trim(),
                notes: editDraft.notes,
                confidence: editDraft.confidence,
              }
            : x,
        ),
      );
      setEditingId(null);
    } catch (err: any) {
      alert(err.message || 'No se pudo guardar');
    } finally {
      setSavingId(null);
    }
  };

  const askDelete = (id: string) => {
    if (confirmDeleteId === id) return; // already confirming
    setConfirmDeleteId(id);
  };

  const confirmDelete = async (id: string) => {
    setSavingId(id);
    try {
      const res = await apiFetch(`/tbit-invoices/api/memorias/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setMemorias((curr) => curr.filter((x) => x._id !== id));
      setEditingId(null);
      setConfirmDeleteId(null);
    } catch (err: any) {
      console.error('Delete failed:', err);
    } finally {
      setSavingId(null);
    }
  };

  const openCreate = () => {
    setCreating(true);
    setCreateDraft(emptyDraft());
    setCreateError(null);
  };

  const cancelCreate = () => {
    setCreating(false);
    setCreateError(null);
  };

  const submitCreate = async () => {
    if (!createDraft.condition.trim() || !createDraft.action.trim()) {
      setCreateError('Las dos primeras líneas son obligatorias.');
      return;
    }
    setSavingId('__new__');
    setCreateError(null);
    try {
      const body: Record<string, unknown> = {
        scope: createDraft.scope,
        condition: createDraft.condition.trim(),
        action: createDraft.action.trim(),
        confidence: createDraft.confidence,
      };
      if (createDraft.notes.trim()) body.notes = createDraft.notes;
      if (
        (createDraft.scope === 'vendor' || createDraft.scope === 'invoice_template') &&
        createDraft.vendor_name.trim()
      ) {
        body.vendor_name = createDraft.vendor_name.trim();
      }
      const res = await apiFetch('/tbit-invoices/api/memorias', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `Error ${res.status}`);
      }
      const data = await res.json();
      setMemorias((curr) => [data.memoria, ...curr]);
      setCreating(false);
    } catch (err: any) {
      setCreateError(err.message || 'No se pudo crear');
    } finally {
      setSavingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return memorias.filter((m) => {
      if (scope !== 'all' && m.scope !== scope) return false;
      if (activeOnly && !m.is_active) return false;
      if (q) {
        const haystack = [m.condition, m.action, m.vendor_name, m.account_id, m.notes]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [memorias, search, scope, activeOnly]);

  const grouped = useMemo(() => {
    const order: Memoria['scope'][] = ['global', 'vendor', 'invoice_template', 'category'];
    const out = new Map<Memoria['scope'], Memoria[]>();
    for (const s of order) out.set(s, []);
    for (const m of filtered) {
      const arr = out.get(m.scope);
      if (arr) arr.push(m);
    }
    return order
      .map((s) => ({ scope: s, items: out.get(s) ?? [] }))
      .filter((g) => g.items.length > 0);
  }, [filtered]);

  const counts = useMemo(() => {
    const total = memorias.length;
    const active = memorias.filter((m) => m.is_active).length;
    const byScope = memorias.reduce<Record<string, number>>((acc, m) => {
      acc[m.scope] = (acc[m.scope] ?? 0) + 1;
      return acc;
    }, {});
    return { total, active, byScope };
  }, [memorias]);

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Reglas contables</h1>
        <div className="card bg-base-100 shadow animate-pulse">
          <div className="card-body space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-4 bg-base-300 rounded w-2/3" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Reglas contables</h1>
        <div className="alert alert-error">
          <span>{error}</span>
          <button className="btn btn-sm" onClick={load}>Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reglas contables</h1>
          <p className="text-sm text-base-content/60 mt-1">
            La IA usa estas reglas como guía al clasificar tus facturas. Vienen del histórico de NetSuite y de tus correcciones.
          </p>
        </div>
        {!creating && (
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            + Nueva regla
          </button>
        )}
      </div>

      {creating && (
        <CreateForm
          draft={createDraft}
          onChange={setCreateDraft}
          onCancel={cancelCreate}
          onSubmit={submitCreate}
          saving={savingId === '__new__'}
          error={createError}
        />
      )}

      <div className="stats stats-horizontal shadow bg-base-100 w-full">
        <div className="stat">
          <div className="stat-title">Total</div>
          <div className="stat-value text-2xl">{counts.total}</div>
        </div>
        <div className="stat">
          <div className="stat-title">Activas</div>
          <div className="stat-value text-2xl text-success">{counts.active}</div>
        </div>
        <div className="stat">
          <div className="stat-title">Inactivas</div>
          <div className="stat-value text-2xl text-base-content/40">{counts.total - counts.active}</div>
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body py-4 flex flex-row flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Buscar por proveedor, cuenta o texto…"
            className="input input-bordered input-sm flex-1 min-w-[240px]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="select select-bordered select-sm"
            value={scope}
            onChange={(e) => setScope(e.target.value as ScopeFilter)}
          >
            <option value="all">Todos los tipos</option>
            <option value="global">Globales ({counts.byScope.global ?? 0})</option>
            <option value="vendor">Por proveedor ({counts.byScope.vendor ?? 0})</option>
            <option value="invoice_template">Recurrentes ({counts.byScope.invoice_template ?? 0})</option>
            <option value="category">Por categoría ({counts.byScope.category ?? 0})</option>
          </select>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            Solo activas
          </label>
        </div>
      </div>

      {grouped.length === 0 ? (
        <div className="card bg-base-100 shadow">
          <div className="card-body items-center text-center py-12">
            <p className="text-base-content/60">No hay reglas que coincidan con el filtro.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.scope} className="card bg-base-100 shadow">
              <div className="card-body">
                <div className="flex items-baseline justify-between">
                  <h2 className="card-title text-base">
                    {SCOPE_LABEL[group.scope]}
                    <span className="badge badge-ghost badge-sm ml-2">{group.items.length}</span>
                  </h2>
                </div>
                <p className="text-xs text-base-content/50 mt-0 mb-3">
                  {SCOPE_DESCRIPTION[group.scope]}
                </p>
                <div className="space-y-2">
                  {group.items.map((m) => (
                    <MemoriaRow
                      key={m._id}
                      memoria={m}
                      editing={editingId === m._id}
                      draft={editDraft}
                      onDraftChange={setEditDraft}
                      onStartEdit={() => startEdit(m)}
                      onCancelEdit={cancelEdit}
                      onSave={() => saveEdit(m._id)}
                      onToggle={() => toggleActive(m)}
                      saving={savingId === m._id}
                      confirmingDelete={confirmDeleteId === m._id}
                      onAskDelete={() => askDelete(m._id)}
                      onConfirmDelete={() => confirmDelete(m._id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

interface RowProps {
  memoria: Memoria;
  editing: boolean;
  draft: Draft;
  onDraftChange: (d: Draft) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onToggle: () => void;
  saving: boolean;
  confirmingDelete: boolean;
  onAskDelete: () => void;
  onConfirmDelete: () => void;
}

function MemoriaRow({
  memoria: m,
  editing,
  draft,
  onDraftChange,
  onStartEdit,
  onCancelEdit,
  onSave,
  onToggle,
  saving,
  confirmingDelete,
  onAskDelete,
  onConfirmDelete,
}: RowProps) {
  if (editing) {
    return (
      <div className="p-3 rounded border-2 border-primary bg-base-50 space-y-3">
        <div>
          <label className="text-xs text-base-content/60">Cuando</label>
          <textarea
            className="textarea textarea-bordered w-full text-sm"
            rows={2}
            autoCorrect="off"
            value={draft.condition}
            onChange={(e) => onDraftChange({ ...draft, condition: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-base-content/60">Acción</label>
          <textarea
            className="textarea textarea-bordered w-full text-sm"
            rows={2}
            autoCorrect="off"
            value={draft.action}
            onChange={(e) => onDraftChange({ ...draft, action: e.target.value })}
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-base-content/60">Confianza</label>
            <select
              className="select select-bordered select-sm w-full"
              value={draft.confidence}
              onChange={(e) => onDraftChange({ ...draft, confidence: e.target.value as Memoria['confidence'] })}
            >
              <option value="high">Alta</option>
              <option value="medium">Media</option>
              <option value="low">Baja</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="text-xs text-base-content/60">Notas (opcional)</label>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              autoCorrect="off"
              value={draft.notes}
              onChange={(e) => onDraftChange({ ...draft, notes: e.target.value })}
            />
          </div>
        </div>
        <div className="flex justify-between items-center pt-1">
          <div>
            {!confirmingDelete ? (
              <button className="btn btn-ghost btn-sm text-error" onClick={onAskDelete} disabled={saving}>
                Eliminar
              </button>
            ) : (
              <button className="btn btn-error btn-sm" onClick={onConfirmDelete} disabled={saving}>
                {saving ? <span className="loading loading-spinner loading-xs" /> : '¿Confirmar eliminación?'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button className="btn btn-ghost btn-sm" onClick={onCancelEdit} disabled={saving}>
              Cancelar
            </button>
            <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
              {saving ? <span className="loading loading-spinner loading-xs" /> : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded border ${
        m.is_active ? 'border-base-300 bg-base-50' : 'border-base-200 bg-base-200/40 opacity-60'
      }`}
    >
      <input
        type="checkbox"
        className="toggle toggle-sm toggle-success mt-1"
        checked={m.is_active}
        disabled={saving}
        onChange={onToggle}
        title={m.is_active ? 'Desactivar regla' : 'Activar regla'}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="text-base-content/70">Cuando:</span>{' '}
          <span className="font-medium">{m.condition}</span>
        </p>
        <p className="text-sm mt-1">
          <span className="text-base-content/70">Acción:</span>{' '}
          {m.action}
        </p>
        {m.notes && <p className="text-xs text-base-content/50 mt-1 italic">{m.notes}</p>}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className={`badge badge-sm ${CONFIDENCE_BADGE[m.confidence]}`}>
            {CONFIDENCE_LABEL[m.confidence]}
          </span>
          <span className="badge badge-sm badge-ghost">{SOURCE_LABEL[m.source]}</span>
          {typeof m.evidence_count === 'number' && m.evidence_count > 0 && (
            <span className="text-xs text-base-content/40">
              {m.evidence_count} {m.evidence_count === 1 ? 'observación' : 'observaciones'}
            </span>
          )}
        </div>
      </div>
      <button className="btn btn-ghost btn-xs" onClick={onStartEdit} title="Editar">
        Editar
      </button>
    </div>
  );
}

interface CreateFormProps {
  draft: Draft;
  onChange: (d: Draft) => void;
  onCancel: () => void;
  onSubmit: () => void;
  saving: boolean;
  error: string | null;
}

function CreateForm({ draft, onChange, onCancel, onSubmit, saving, error }: CreateFormProps) {
  const showVendorField = draft.scope === 'vendor' || draft.scope === 'invoice_template';
  return (
    <div className="card bg-base-100 shadow border-2 border-primary">
      <div className="card-body space-y-3">
        <h2 className="card-title text-base">Nueva regla</h2>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-base-content/60">Tipo</label>
            <select
              className="select select-bordered select-sm w-full"
              value={draft.scope}
              onChange={(e) => onChange({ ...draft, scope: e.target.value as Memoria['scope'] })}
            >
              <option value="global">Global</option>
              <option value="vendor">Por proveedor</option>
              <option value="invoice_template">Proveedor recurrente</option>
              <option value="category">Por categoría</option>
            </select>
            <p className="text-xs text-base-content/50 mt-1">
              {SCOPE_DESCRIPTION[draft.scope]}
            </p>
          </div>
          <div className="flex-1">
            <label className="text-xs text-base-content/60">Confianza</label>
            <select
              className="select select-bordered select-sm w-full"
              value={draft.confidence}
              onChange={(e) => onChange({ ...draft, confidence: e.target.value as Memoria['confidence'] })}
            >
              <option value="high">Alta</option>
              <option value="medium">Media</option>
              <option value="low">Baja</option>
            </select>
          </div>
        </div>
        {showVendorField && (
          <div>
            <label className="text-xs text-base-content/60">Proveedor (nombre exacto en NetSuite)</label>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              autoCorrect="off"
              placeholder="Ej: COMCEL SA"
              value={draft.vendor_name}
              onChange={(e) => onChange({ ...draft, vendor_name: e.target.value })}
            />
          </div>
        )}
        <div>
          <label className="text-xs text-base-content/60">Cuando *</label>
          <textarea
            className="textarea textarea-bordered w-full text-sm"
            rows={2}
            autoCorrect="off"
            placeholder='Ej: "Cuando el proveedor es COMCEL SA"'
            value={draft.condition}
            onChange={(e) => onChange({ ...draft, condition: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-base-content/60">Acción *</label>
          <textarea
            className="textarea textarea-bordered w-full text-sm"
            rows={2}
            autoCorrect="off"
            placeholder='Ej: "Mapear a la cuenta NS 759 - Telefonía"'
            value={draft.action}
            onChange={(e) => onChange({ ...draft, action: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-base-content/60">Notas (opcional)</label>
          <input
            type="text"
            className="input input-bordered input-sm w-full"
            autoCorrect="off"
            value={draft.notes}
            onChange={(e) => onChange({ ...draft, notes: e.target.value })}
          />
        </div>
        {error && <p className="text-error text-sm">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button className="btn btn-primary btn-sm" onClick={onSubmit} disabled={saving}>
            {saving ? <span className="loading loading-spinner loading-xs" /> : 'Crear regla'}
          </button>
        </div>
      </div>
    </div>
  );
}
